import { InputHTMLAttributes } from "react";

import { InputAdornment, TextField, Typography } from "@mui/material";
import classNames from "classnames";
import {
	Control,
	Controller,
	FieldPath,
	FieldValues,
	RegisterOptions,
} from "react-hook-form";

import { getFormError } from "../../../utils";

export type TCommonInputProps<T extends FieldValues> = {
	control: Control<T>;
	name: FieldPath<T>;
	rules?: Omit<
		RegisterOptions<T, FieldPath<T>>,
		"valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
	>;
	placeholder?: string;
	type?: InputHTMLAttributes<unknown>["type"];
	disabled?: boolean;
	className?: string;
	prefix?: string;
	suffix?: string;
	multiline?: boolean;
	successMessage?: boolean;
	disallowInvalidInput?: boolean;
};

export default function CommonInput<T extends FieldValues>({
	control,
	name,
	rules = {},
	placeholder = "",
	type = "text",
	disabled = false,
	className = "",
	prefix,
	suffix,
	multiline = false,
	disallowInvalidInput = false,
}: TCommonInputProps<T>) {
	return (
		<Controller
			control={control}
			name={name}
			rules={rules}
			render={({ field: { value, onChange }, formState: { errors } }) => {
				const errorMessage = getFormError(name, errors);

				let newClassName = className;
				if (errorMessage) {
					newClassName += " invalid";
				}

				return (
					<div className="common-input">
						<TextField
							type={type}
							className={newClassName}
							placeholder={placeholder}
							value={value}
							onChange={(e) => {
								if (
									disallowInvalidInput &&
									e.target.value !== "" &&
									// eslint-disable-next-line @typescript-eslint/ban-ts-comment
									// @ts-ignore
									!rules.pattern?.value?.test(e.target.value)
								) {
									return;
								}

								onChange(e);
							}}
							fullWidth
							multiline={multiline}
							disabled={disabled}
							minRows={2}
							slotProps={{
								input: {
									startAdornment: prefix && (
										<InputAdornment position="start">
											<Typography
												className={classNames("normal-text", className)}>
												{prefix}
											</Typography>
										</InputAdornment>
									),
									endAdornment: suffix && (
										<InputAdornment position="end">
											<Typography
												className={classNames("normal-text", className)}>
												{suffix}
											</Typography>
										</InputAdornment>
									),
								},
							}}
						/>
						{errorMessage && (
							<Typography className="invalid-message">
								{errorMessage}
							</Typography>
						)}
					</div>
				);
			}}
		/>
	);
}
